import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { PORTFOLIO_ITEMS, BRAND_COLOR } from '../constants';
import { PortfolioItem } from '../types';

const Portfolio: React.FC<{ isDarkMode: boolean }> = ({ isDarkMode }) => {
  const [filter, setFilter] = useState('ALL');
  const [selectedItem, setSelectedItem] = useState<PortfolioItem | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);
  
  const cats = ['ALL', 'PORTRAITS', 'CREATORS', 'PRODUCTS', 'EVENTS'];
  const items = filter === 'ALL' ? PORTFOLIO_ITEMS : PORTFOLIO_ITEMS.filter(i => i.category === filter);

  const nextImage = useCallback(() => {
    if (!selectedItem) return;
    setCurrentIndex((prev) => (prev + 1) % selectedItem.sessionImages.length);
  }, [selectedItem]);

  const prevImage = useCallback(() => {
    if (!selectedItem) return;
    setCurrentIndex((prev) => (prev - 1 + selectedItem.sessionImages.length) % selectedItem.sessionImages.length);
  }, [selectedItem]);

  const downloadCurrentImage = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!selectedItem || isDownloading) return;
    
    const imageUrl = selectedItem.sessionImages[currentIndex];
    const fileName = `${selectedItem.title.replace(/\s+/g, '-').toLowerCase()}-${currentIndex + 1}.jpg`;
    
    try {
      setIsDownloading(true);
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download failed:", error);
      window.open(imageUrl, '_blank');
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!selectedItem) return;
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedItem, nextImage, prevImage]);

  const handleOpen = (item: PortfolioItem) => {
    setSelectedItem(item);
    setCurrentIndex(0);
    document.body.style.overflow = 'hidden';
  };

  const handleClose = () => {
    setSelectedItem(null);
    document.body.style.overflow = 'unset';
  };

  return (
    <section id="work" className={`py-32 border-t transition-colors duration-500 ${isDarkMode ? 'bg-black border-white/5' : 'bg-white border-zinc-100'}`}>
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-20 gap-8">
          <motion.h2 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className={`font-display text-5xl md:text-6xl font-black uppercase tracking-tighter italic transition-colors duration-500 ${isDarkMode ? 'text-white' : 'text-black'}`}
          >
            THE <span style={{ color: BRAND_COLOR }}>ARCHIVE</span>
          </motion.h2>
          <div className="flex flex-wrap gap-8 md:gap-10 text-[13px] md:text-[14px] font-black uppercase tracking-[0.3em] md:tracking-[0.35em]">
            {cats.map(c => (
              <button 
                key={c} 
                onClick={() => setFilter(c)} 
                className={`transition-colors relative pb-3 font-black ${filter === c ? 'text-brand' : isDarkMode ? 'text-white' : 'text-black'}`}
              >
                {c}
                {filter === c && <motion.div layoutId="underline" className="absolute bottom-0 left-0 right-0 h-[2px] bg-brand" />}
              </button>
            ))}
          </div>
        </div>

        <motion.div layout className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          <AnimatePresence mode="popLayout">
            {items.map((i: PortfolioItem) => (
              <motion.div 
                key={i.id} 
                layout 
                initial={{ opacity: 0, y: 20 }} 
                animate={{ opacity: 1, y: 0 }} 
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.6 }}
                onClick={() => handleOpen(i)}
                className={`aspect-[3/4] group relative overflow-hidden border cursor-pointer transition-colors duration-500 ${isDarkMode ? 'bg-black border-white/5' : 'bg-white border-zinc-100'}`}
              >
                <img 
                  src={i.coverImage} 
                  className="w-full h-full object-cover group-hover:scale-105 transition-all duration-1000" 
                  alt={i.title}
                />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-end p-10">
                   <p style={{ color: BRAND_COLOR }} className="text-[12px] font-black tracking-widest uppercase mb-2">{i.category}</p>
                   <h3 className="text-3xl font-display font-black uppercase text-white">{i.title}</h3>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      <AnimatePresence>
        {selectedItem && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 z-[150] bg-black flex flex-col items-center justify-center cursor-default overflow-hidden"
          >
            <div className="absolute inset-0 z-0">
               <motion.img 
                 key={selectedItem.sessionImages[currentIndex]}
                 initial={{ opacity: 0 }}
                 animate={{ opacity: 0.3 }}
                 src={selectedItem.sessionImages[currentIndex]} 
                 className="w-full h-full object-cover blur-[100px] scale-110"
               />
            </div>

            <div className="absolute top-6 right-6 md:top-10 md:right-10 z-[170] flex gap-4">
              <motion.button 
                className={`p-4 bg-black/20 backdrop-blur-md rounded-full border border-white/10 hover:scale-110 transition-all active:scale-95 text-white ${isDownloading ? 'opacity-50 cursor-wait' : 'opacity-100'}`}
                onClick={downloadCurrentImage}
                whileTap={{ scale: 0.9 }}
                title="Download Image"
              >
                {isDownloading ? (
                  <div className="w-6 h-6 border-2 border-white/20 border-t-white rounded-full animate-spin" />
                ) : (
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3" />
                  </svg>
                )}
              </motion.button>

              <motion.button 
                className="p-4 bg-black/20 backdrop-blur-md rounded-full border border-white/10 hover:scale-110 transition-all active:scale-95 text-white"
                onClick={handleClose}
                whileTap={{ scale: 0.9 }}
                title="Close Archive"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M18 6L6 18M6 6l12 12" />
                </svg>
              </motion.button>
            </div>

            <button 
              className="hidden md:flex absolute left-6 lg:left-12 top-1/2 -translate-y-1/2 z-[160] text-white/50 hover:text-white p-6 transition-all hover:scale-110 active:scale-90 bg-white/5 backdrop-blur-md rounded-full border border-white/5"
              onClick={(e) => { e.stopPropagation(); prevImage(); }}
            >
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <button 
              className="hidden md:flex absolute right-6 lg:right-12 top-1/2 -translate-y-1/2 z-[160] text-white/50 hover:text-white p-6 transition-all hover:scale-110 active:scale-90 bg-white/5 backdrop-blur-md rounded-full border border-white/5"
              onClick={(e) => { e.stopPropagation(); nextImage(); }}
            >
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                <path d="M9 18l6-6-6-6" />
              </svg>
            </button>
            
            <div 
              className="relative w-full max-w-6xl h-full flex flex-col items-center justify-between p-4 md:p-8 z-10"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="md:hidden pt-8 text-center">
                <p className="text-zinc-500 font-black text-[10px] tracking-[0.4em]">{currentIndex + 1} / {selectedItem.sessionImages.length}</p>
              </div>

              <div className="relative w-full flex-grow flex items-center justify-center py-4 md:py-8 overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.div
                    key={currentIndex}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.05 }}
                    transition={{ type: 'spring', damping: 30, stiffness: 200 }}
                    drag="x"
                    dragConstraints={{ left: 0, right: 0 }}
                    onDragEnd={(_, info) => {
                      if (info.offset.x > 80) prevImage();
                      else if (info.offset.x < -80) nextImage();
                    }}
                    className="w-full h-full flex items-center justify-center cursor-grab active:cursor-grabbing"
                  >
                    <img 
                      src={selectedItem.sessionImages[currentIndex]} 
                      className="w-auto h-auto max-w-full max-h-full object-contain shadow-[0_40px_100px_-20px_rgba(0,0,0,0.8)] select-none border border-white/5"
                      draggable={false}
                    />
                  </motion.div>
                </AnimatePresence>
                
                <div className="md:hidden absolute inset-0 flex">
                  <div className="w-1/2 h-full" onClick={prevImage} />
                  <div className="w-1/2 h-full" onClick={nextImage} />
                </div>
              </div>

              <div className="w-full flex flex-col items-center gap-6 md:gap-10 pb-6 md:pb-12 bg-gradient-to-t from-black/80 via-black/40 to-transparent">
                <div className="text-center px-4">
                  <motion.p 
                    layoutId="cat"
                    style={{ color: BRAND_COLOR }} 
                    className="text-[10px] md:text-[12px] font-black tracking-[0.5em] uppercase mb-1 drop-shadow-md"
                  >
                    {selectedItem.category}
                  </motion.p>
                  <motion.h3 
                    layoutId="title"
                    className="text-3xl md:text-6xl font-display font-black uppercase text-white tracking-tighter mb-2 drop-shadow-lg"
                  >
                    {selectedItem.title}
                  </motion.h3>
                  <p className="hidden md:block text-zinc-400 font-black text-[11px] tracking-[0.3em] uppercase">{currentIndex + 1} / {selectedItem.sessionImages.length}</p>
                </div>

                <div className="w-full max-w-md md:max-w-xl flex justify-center">
                  <div className="flex gap-2.5 md:gap-4 px-6 overflow-x-auto no-scrollbar scroll-smooth py-2">
                    {selectedItem.sessionImages.map((img: string, idx: number) => (
                      <button
                        key={idx}
                        onClick={() => setCurrentIndex(idx)}
                        className={`relative w-14 h-14 md:w-24 md:h-24 flex-shrink-0 border-2 transition-all duration-300 group overflow-hidden ${idx === currentIndex ? 'border-brand scale-110' : 'border-white/10 opacity-40 hover:opacity-100 hover:border-white/40'}`}
                      >
                        <img src={img} className="w-full h-full object-cover grayscale-[0.3] group-hover:grayscale-0 transition-all" alt={`Session thumb ${idx}`} />
                        {idx === currentIndex && (
                          <div className="absolute inset-0 bg-brand/10 ring-1 ring-inset ring-brand/50" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
                
                <button 
                  onClick={handleClose}
                  className="md:hidden text-zinc-500 text-[10px] font-black tracking-[0.3em] uppercase border-b border-zinc-800 pb-1 mt-2"
                >
                  TAP TO EXIT
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </section>
  );
};

export default Portfolio;